import { useState, useEffect } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Search, ShieldCheck, Ban, RotateCcw, ChevronLeft, ChevronRight, Loader2, Bot, Eye, Trash2 } from 'lucide-react';

const ROLE_LABELS = {
  buyer: 'Comprador', seller: 'Vendedor', seller_basic: 'Seller Basic',
  seller_premium: 'Seller Premium', elite_breeder: 'Elite', admin: 'Admin', super_admin: 'Super Admin',
  enbixos_operator: 'Enbixos Op',
};
const ROLE_COLORS = {
  buyer: 'bg-zinc-500/10 text-zinc-400', seller: 'bg-blue-500/10 text-blue-400',
  seller_basic: 'bg-emerald-500/10 text-emerald-400', seller_premium: 'bg-purple-500/10 text-purple-400',
  elite_breeder: 'bg-amber-500/10 text-amber-400', admin: 'bg-red-500/10 text-red-400',
  super_admin: 'bg-red-600/10 text-red-400', enbixos_operator: 'bg-orange-500/10 text-orange-400',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => { load(); }, [page, roleFilter, subFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.adminGetUsers({ page, limit: 20, search: search || undefined, role: roleFilter !== 'all' ? roleFilter : undefined, subscription: subFilter !== 'all' ? subFilter : undefined });
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); load(); };

  const handleAction = async (action, userId) => {
    try {
      if (action === 'suspend') {
        const resp = await api.suspendUser(userId, reason);
        toast.success(resp.message || 'Usuario suspendido');
      } else if (action === 'reactivate') {
        await api.reactivateUser(userId);
        toast.success('Usuario reactivado');
      } else if (action === 'delete') {
        const resp = await api.deleteUser(userId);
        toast.success(resp.message || 'Usuario eliminado');
      }
      setActionDialog(null); setReason(''); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleChangePlan = async (userId, plan) => {
    try {
      await api.adminChangePlan(userId, { plan_id: plan });
      toast.success(`Plan cambiado a ${plan}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleViewAs = async (user) => {
    try {
      const data = await api.adminViewAs(user.id);
      // Save current admin token
      const currentToken = localStorage.getItem('access_token');
      localStorage.setItem('admin_original_token', currentToken);
      // Set impersonation token
      localStorage.setItem('access_token', data.token);
      localStorage.setItem('impersonating', JSON.stringify({ name: user.name, email: user.email }));
      window.location.href = '/dashboard';
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-users-page">
      <h1 className="text-xl font-semibold text-zinc-100 mb-1">Gestion de Usuarios</h1>
      <p className="text-xs text-zinc-600 mb-6">{total} usuarios registrados</p>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <Input placeholder="Buscar por nombre o email..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/[0.03] border-white/5 text-zinc-100 h-9 text-sm" data-testid="users-search" />
          </div>
        </form>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm"><SelectValue placeholder="Rol" /></SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos los roles</SelectItem>
            <SelectItem value="buyer">Comprador</SelectItem>
            <SelectItem value="seller">Vendedor</SelectItem>
            <SelectItem value="seller_basic">Basic</SelectItem>
            <SelectItem value="seller_premium">Premium</SelectItem>
            <SelectItem value="elite_breeder">Elite</SelectItem>
          </SelectContent>
        </Select>
        <Select value={subFilter} onValueChange={v => { setSubFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos los planes</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
            <SelectItem value="elite">Elite</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.04]">
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Usuario</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Rol</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Plan</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">IA</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Strikes</th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Estado</th>
                <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="border-b border-white/[0.02] hover:bg-white/[0.01] transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-zinc-200 text-xs font-medium">{u.name}</p>
                      <p className="text-zinc-600 text-[10px]">{u.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${ROLE_COLORS[u.role] || 'bg-zinc-500/10 text-zinc-400'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Select defaultValue={u.subscription || 'free'} onValueChange={v => handleChangePlan(u.id, v)}>
                      <SelectTrigger className="w-24 h-7 text-[10px] bg-transparent border-white/5"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121215] border-white/10">
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="basic">Basic</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="elite">Elite</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {u.ai_features?.enabled ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">
                        <Bot className="w-3 h-3 inline mr-0.5" />{u.ai_features.tier}
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-700">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${(u.strike_count || 0) >= 3 ? 'text-red-400 font-medium' : 'text-zinc-500'}`}>
                      {u.strike_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.status === 'suspended' ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Suspendido</span>
                    ) : (
                      <span className="text-[10px] text-emerald-400">Activo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {u.role !== 'super_admin' && u.role !== 'admin' && u.status !== 'suspended' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-amber-400 hover:bg-amber-500/10"
                          onClick={() => handleViewAs(u)} data-testid={`view-as-${u.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Ver como
                        </Button>
                      )}
                      {u.status === 'suspended' ? (
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => handleAction('reactivate', u.id)} data-testid={`reactivate-${u.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Reactivar
                        </Button>
                      ) : u.role !== 'super_admin' && u.role !== 'admin' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-400 hover:bg-red-500/10"
                          onClick={() => setActionDialog({ action: 'suspend', userId: u.id, name: u.name })} data-testid={`suspend-${u.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Suspender
                        </Button>
                      )}
                      {u.role !== 'super_admin' && u.role !== 'admin' && (
                        <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-500 hover:bg-red-500/10"
                          onClick={() => setActionDialog({ action: 'delete', userId: u.id, name: u.name, email: u.email })} data-testid={`delete-${u.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
          <span className="text-xs text-zinc-600">Pagina {page} de {Math.ceil(total / 20)}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs text-zinc-400">
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <Button size="sm" variant="ghost" disabled={users.length < 20} onClick={() => setPage(p => p + 1)} className="h-7 text-xs text-zinc-400">
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Suspend / Delete Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => setActionDialog(null)}>
        <DialogContent className="bg-[#121215] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              {actionDialog?.action === 'delete' ? `Eliminar a ${actionDialog?.name}` : `Suspender a ${actionDialog?.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {actionDialog?.action === 'delete' ? (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-xs text-red-300">
                  Esta accion es IRREVERSIBLE. Se eliminara al usuario <strong>{actionDialog?.email}</strong> y TODAS sus publicaciones, mensajes, favoritos y tickets permanentemente.
                </p>
              </div>
            ) : (
              <Input placeholder="Razon de suspension..." value={reason} onChange={e => setReason(e.target.value)}
                className="bg-white/5 border-white/10 text-zinc-100" data-testid="suspend-reason" />
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setActionDialog(null)} className="border-white/10 text-zinc-400">Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-500 text-white"
                onClick={() => handleAction(actionDialog.action, actionDialog.userId)}
                data-testid={`confirm-${actionDialog?.action}`}>
                {actionDialog?.action === 'delete' ? 'Eliminar permanentemente' : 'Suspender usuario'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
